import type { ReactNode } from 'react';
import type { UseQueryResult } from '@tanstack/react-query';
import {
  Card,
  ErrorState,
  FilterDivider,
  GrainPicker,
  Legend,
  LoadingState,
  PairedBars,
  PanelTitle,
  RangePicker,
  calendarChipLabel,
  isCalendarPeriod,
  type DeltaTone,
  type RangeValue,
} from '../../components';
import { useSession } from '../../lib/auth/useSession';
import {
  calendarRange,
  customRangeDays,
  isCustomRange,
  presetRange,
  today,
  type DayRange,
} from '../../lib/format/dates';
import { formatChange } from '../../lib/format/money';
import { useTableUrlState } from '../../lib/table/tableSearch';
import { listParam, pickParam } from '../profiles/people';
import type { AnalyticsWindow, Bucket, TimeSeries } from './api';

/**
 * The analytics window lives in the URL: `?range=` (7d | 30d | 90d | all, or a
 * calendar period such as mtd / last-quarter), `?bucket=day|week|month`,
 * `?localeIds=`. Dates are the admin's profile timezone (the BE reads
 * startDate/endDate in the caller's zone). A value equal to the screen's
 * default is left out of the URL.
 *
 * "All" needs an explicit floor: with no startDate the BE falls back to the
 * trailing 30 days, which would make "All" silently mean "30d".
 */
export const ALL_TIME_START = '2025-01-01';

const PRESETS = ['7d', '30d', '90d', 'all'] as const;
const BUCKETS = ['day', 'week', 'month'] as const;

function readRange(value: unknown): RangeValue | undefined {
  if (typeof value !== 'string') return undefined;
  return (PRESETS as readonly string[]).includes(value) || isCalendarPeriod(value) || isCustomRange(value)
    ? (value as RangeValue)
    : undefined;
}

/** Inclusive days for a range value, in the admin's timezone. */
export function rangeDays(range: RangeValue, tz?: string): DayRange {
  if (range === 'all') return { start: ALL_TIME_START, end: today(tz) };
  if (isCalendarPeriod(range)) return calendarRange(range, tz);
  if (isCustomRange(range)) return customRangeDays(range);
  return presetRange(range, tz);
}

/**
 * `utc`: the screen's days are UTC (the Money screens) rather than the admin's
 * timezone, so "today" and the presets are counted in UTC.
 */
export function useAnalyticsWindow(defaults: { range?: RangeValue; bucket?: Bucket; utc?: boolean } = {}) {
  const table = useTableUrlState();
  const { profile } = useSession();
  const tz = defaults.utc ? 'UTC' : profile?.timezone;
  const defaultRange = defaults.range ?? '30d';
  const defaultBucket = defaults.bucket ?? 'day';
  const range = readRange(table.filters.range) ?? defaultRange;
  const bucket = pickParam(table.filters.bucket, BUCKETS) ?? defaultBucket;
  const localeIds = listParam(table.filters.localeIds);
  const days = rangeDays(range, tz);

  const window: AnalyticsWindow = { localeIds, startDate: days.start, endDate: days.end, bucket };
  return {
    window,
    range,
    bucket,
    localeIds,
    timeZone: tz,
    setRange: (r: RangeValue) => table.setParams({ range: r === defaultRange ? undefined : r }),
    setBucket: (b: Bucket) => table.setParams({ bucket: b === defaultBucket ? undefined : b }),
    setLocaleIds: (ids: string[]) => table.setParams({ localeIds: ids.join(',') || undefined }),
  };
}

const PRESET_TITLE: Record<(typeof PRESETS)[number], string> = {
  '7d': 'Last 7 days',
  '30d': 'Last 30 days',
  '90d': 'Last 90 days',
  all: 'All time',
};

/** Human name of a range: "Last 7 days", "Month to date", "All time". */
export function rangeTitle(range: RangeValue): string {
  if (isCalendarPeriod(range) || isCustomRange(range)) return calendarChipLabel(range) ?? range;
  return PRESET_TITLE[range];
}

/** Range chips (with the calendar popover) | Grain chips — the same control wherever a series is drawn. */
export function WindowControls({ w }: { w: ReturnType<typeof useAnalyticsWindow> }) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <RangePicker value={w.range} onChange={w.setRange} />
      <FilterDivider />
      <GrainPicker value={w.bucket} onChange={w.setBucket} />
    </div>
  );
}

/** A dashboard card that owns its loading and error states. */
export function QueryCard<T>({
  eyebrow,
  title,
  actions,
  query,
  scroll = true,
  children,
}: {
  eyebrow?: ReactNode;
  title: ReactNode;
  actions?: ReactNode;
  query: UseQueryResult<T>;
  scroll?: boolean;
  children: (data: T) => ReactNode;
}) {
  return (
    <Card
      surface="panel"
      scroll={scroll}
      header={
        <div className="flex-1">
          <PanelTitle eyebrow={eyebrow} actions={actions}>
            {title}
          </PanelTitle>
        </div>
      }
    >
      {query.isPending ? (
        <LoadingState />
      ) : query.isError ? (
        <ErrorState error={query.error} onRetry={() => void query.refetch()} compact />
      ) : (
        <>{children(query.data)}</>
      )}
    </Card>
  );
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** Bucket label. Buckets are UTC-truncated on the BE, so they are read in UTC. */
export function formatBucket(iso: string, bucket: Bucket): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const month = MONTHS[d.getUTCMonth()] ?? '';
  return bucket === 'month' ? `${month} ${String(d.getUTCFullYear()).slice(2)}` : `${month} ${d.getUTCDate()}`;
}

/** Thins labels so a long series stays readable (about 8 labels at most). */
export function thinLabels(labels: string[], max = 8): string[] {
  const step = Math.max(1, Math.ceil(labels.length / max));
  return labels.map((l, i) => (i % step === 0 ? l : ''));
}

/**
 * Two series of the same buckets as paired bars. Each series is scaled to its
 * own maximum (the BE is explicit that e.g. listings and portions do not
 * belong on one axis); the legend carries each total and period change.
 */
export function SeriesPair({
  a,
  b,
  aLabel,
  bLabel,
  bucket,
  height = 150,
}: {
  a: TimeSeries;
  b: TimeSeries;
  aLabel: string;
  bLabel: string;
  bucket: Bucket;
  height?: number;
}) {
  const labels = thinLabels(a.points.map((p) => formatBucket(p.bucket, bucket)));
  return (
    <>
      <PairedBars
        a={a.points.map((p) => p.count)}
        b={b.points.map((p) => p.count)}
        labels={labels}
        aLabel={aLabel}
        bLabel={bLabel}
        height={height}
        independentScales
      />
      <div className="mt-3 border-t border-[rgba(143,68,32,.1)] pt-3">
        <Legend
          items={[
            [`${aLabel}: ${a.total.toLocaleString()} (${formatChange(a.changePct)} vs prior)`, 'var(--admin-chart-1)'],
            [`${bLabel}: ${b.total.toLocaleString()} (${formatChange(b.changePct)} vs prior)`, 'rgba(127,150,254,.65)'],
          ]}
        />
        <div className="mt-1 text-xs text-muted">Each series is scaled to its own maximum.</div>
      </div>
    </>
  );
}

/** "2d 6h" from hours. */
export function formatHours(hours: number | null | undefined): string {
  if (hours === null || hours === undefined || !Number.isFinite(hours)) return '—';
  const total = Math.round(hours);
  const d = Math.floor(total / 24);
  const h = total % 24;
  return d > 0 ? `${d}d ${h}h` : `${h}h`;
}

/** Delta tone for a percent change (null = no comparison). */
export function changeTone(pct: number | null | undefined): DeltaTone {
  if (pct === null || pct === undefined || pct === 0) return 'neutral';
  return pct > 0 ? 'up' : 'down';
}
